/**
 * SELF-ANALYSIS state (fix D / R4) — V2-only, WEB-only, and deliberately NOT in
 * the shared core: it is a surface feature (an engine the player turns on after
 * their own game), it holds no socket state, and the app lane has no Stockfish
 * build to drive it.
 *
 * Why a store and not React state: the three consumers are in three different
 * branches of the tree (the Moves tab's header + lines, the Stage's eval bar,
 * one chip per ply in the notation) and every one of them has to be a zero-prop
 * memo leaf with its own subscription — exactly the reason `useV2Ui` exists.
 *
 * FAIR PLAY is the first invariant: `on` is only ever raised by a control that
 * is itself gated on "the game on the board is over" (`rail/actions/tiles/
 * AnalysisAction.model.ts`), and it is forced down the moment a new game spawns
 * (`rail/Rail/model.ts`). Nothing in here subscribes to the game, so a live game
 * pays exactly one boolean read for the whole feature.
 *
 * SCORE CONVENTION: every score in here is CENTIPAWNS FROM WHITE'S POINT OF
 * VIEW (mate scores are signed mate distance, still white-POV). Standard UCI
 * reports the side to move's POV, so `useEngine` flips on `baseTurn` once, at
 * the edge — the three readers then share one convention.
 *
 * THE BRANCH (fix F-C): one variation at a time, like chess.com's quick
 * analysis — "what could have happened else what's the point of analysis".
 * It is INERT while `on` is false (`setOn`/`reset` clear it), so a live game's
 * one boolean subscription is still the whole cost of the feature. Applying a
 * move to the board, and the SAN/cursor navigation around it, are pure helpers
 * in `branch.ts` and `analysisMove.ts` — nothing here knows about chess.js.
 *
 * THE MODE (fix F-D / D3): `mode` is 'off' | 'analysis' | 'assist'. `on` stays
 * the one public boolean every existing reader already uses
 * (`on === (mode !== 'off')`), so F-C's branch logic, `EngineHost`'s mount
 * gate and the eval bar's "does it exist" check all keep working unmodified.
 * `setOn` is the manual toggle (off <-> 'analysis'); `setMode('assist')` is
 * `hooks/useAssistEffects.ts`'s own call the moment an assisted game reaches
 * 'playing' — the live eval bar, with none of self-analysis's EXTRAS: the
 * rail's own `mode === 'analysis'` checks (not `on`) are what keep the branch
 * UI, `EngineLines` and the wide rail OFF during a live assisted game, and
 * `stage/BoardFrame/model.ts`'s board override inert (`actions.move` stays
 * wired) — fair play never depended on `on` alone to begin with (R4's own
 * invariant is "no `StockfishEngine` in the tree during MY OWN unsettled
 * game", which the assist flow does not change: Caissa is the opponent, the
 * game is unrated, and the gate stays untouched).
 *
 * THE FINISH-THEN-ADVANCE QUEUE (fix L / L1, 2026-10-06): a live assisted
 * game used to jump the engine straight to the current position on every
 * move, so the ply right after mine got whatever depth the bot's near-
 * instant reply allowed before the search restarted — often none at all.
 * `assistQueue` is a FIFO of positions still owed a settled score; the HEAD
 * is the one actually under the engine (`useEngine` reads it, never the
 * live position directly), and `settleAssist` commits exactly one chip for
 * it — at `bestmove`, or a watchdog once `lines[0]` has reached
 * `ASSIST_MIN_SETTLED_DEPTH` — before shifting to the next. A budget is
 * decided ONCE, the moment a position BECOMES the head, and never changes
 * while it stays head: `StockfishEngine`'s own restart effect treats a
 * `movetime` prop edit as a reason to stop and restart the SAME search, so
 * re-deciding it mid-search would cut short the very search it is meant to
 * time. The eval bar may therefore lag the board by one search (≤ 2.5 s)
 * after a fast reply — BY DESIGN: the chip of the move before it is what the
 * learner is owed, not a shallower number rushed out to stay in sync with
 * the board.
 *
 * THE TERMINAL VERDICT (fix U, 2026-10-06): a checkmated/stalemated/drawn
 * position is decided on the CLIENT, before it ever reaches the engine —
 * Stockfish answers one with `bestmove (none)` and no usable score, which is
 * why the card used to go blank (no eval, no line) the moment a browsed or
 * assisted position was the game's own last one. `setPosition` checks
 * `terminalVerdict` (the pure chess.js helper in `./branch`) on every browse
 * and, for a terminal position, records `terminal` INSTEAD of starting a
 * search (`thinking` stays false, `lines` stays empty) — `useEngine` reads
 * the same helper to turn `searchEnabled` off for the exact same FEN, so the
 * two can never disagree about which positions are worth asking. Stepping a
 * ply backward out of the terminal position is just another `setPosition`
 * call, so normal analysis resumes on the warm, still-running worker.
 */
import {create} from 'zustand';

import {terminalVerdict} from './branch';

/** the depth and the line count the rail asks Stockfish for (R4) */
export const ANALYSIS_DEPTH = 18;
export const ANALYSIS_LINES = 3;
/** the hard per-search cap: long enough for depth 18, short enough to feel live */
export const ANALYSIS_MOVETIME_MS = 1500;

/**
 * fix L / L1: the live eval bar during an assisted game — shallower than
 * self-analysis's 3-line readout since it runs continuously through the
 * game rather than once after it (ARCHITECTURE §12).
 */
export const ASSIST_DEPTH = 20;
export const ASSIST_LINES = 1;
/** a HEAD's budget while nothing else is queued behind it */
export const ASSIST_MOVETIME_MS = 2500;
/** a HEAD's budget once at least one more position is already waiting */
export const ASSIST_BACKLOG_MOVETIME_MS = 1000;
/** a reconnect's multi-move jump must not queue 20 x 1s of catch-up */
export const ASSIST_QUEUE_MAX = 4;
/** a watchdog settle below this depth ships no chip rather than a shallow one */
export const ASSIST_MIN_SETTLED_DEPTH = 12;
/** grace beyond the head's own budget before the watchdog settles it anyway */
export const ASSIST_SETTLE_GRACE_MS = 3000;

export type EngineLine = {
  /** the principal variation as SAN, from the analysed position */
  san: string[];
  /** centipawns (or signed mate distance), WHITE-POV */
  score: number;
  isMate: boolean;
  /** fix L / L1: parsed from the raw `info` line (`useEngine.onResult`); 0 when absent */
  depth: number;
};

/**
 * fix L / L1: one position still owed a settled score, in `assistQueue`'s
 * FIFO order — index 0 (the head) is the one actually under the engine.
 */
export type AssistSearch = {fen: string; ply: number; budgetMs: number};

export type PlyEval = {
  score: number;
  isMate: boolean;
};

/** one half-move of the analysis branch (F-C / C1) — chess.js's own shape. */
export type BranchMove = {san: string; fen: string; from: string; to: string};

/**
 * fix U: how a position's game ends, decided by `./branch`'s
 * `terminalVerdict` — never by the engine. `winner` only exists on
 * 'checkmate' (the side NOT to move delivered it); every draw kind is a flat
 * tag since there is no side to attribute it to.
 */
export type TerminalVerdict =
  | {kind: 'checkmate'; winner: 'w' | 'b'}
  | {kind: 'stalemate' | 'insufficient' | 'repetition' | 'fifty-move'};

/**
 * ONE variation at a time, like chess.com's quick analysis: `basePly` /
 * `baseFen` are fixed at the ply the player branched FROM, `moves` is the
 * line played since, and `cursor` is how many of them are on the board
 * (0..moves.length) — stepping back to 0 shows `baseFen` again without
 * dropping the line, so stepping forward again replays it.
 */
export type Branch = {
  basePly: number;
  baseFen: string;
  moves: BranchMove[];
  cursor: number;
};

/** fix F-D / D3: 'assist' is the live eval bar during an assisted game. */
export type AnalysisMode = 'off' | 'analysis' | 'assist';

export type AnalysisStore = {
  mode: AnalysisMode;
  /** the public boolean every existing reader keeps using: `mode !== 'off'` */
  on: boolean;
  /** the position being analysed — the browsed ply, not necessarily the last */
  fen: string;
  /** which ply that position is after (the `evalsByPly` key) */
  ply: number;
  depth: number;
  /** up to `ANALYSIS_LINES`, index 0 = multipv 1 */
  lines: EngineLine[];
  /** the engine's chosen move, UCI (`e2e4`) — drawn as an arrow if the board can */
  bestMoveUci: string | null;
  /** every ply the player has browsed to while the engine was on */
  evalsByPly: Record<number, PlyEval>;
  /** a search is out and no line has come back for THIS position yet */
  thinking: boolean;
  /** fix U: how `fen` ends the game, or null for an ordinary position */
  terminal: TerminalVerdict | null;
  /** the one alternative line the player is exploring, or none (F-C / C1) */
  branch: Branch | null;
  /** fix L / L1: positions still owed a settled score; index 0 is the head */
  assistQueue: AssistSearch[];
  /**
   * fix Z1.2: the LAST chip `settleAssist` committed — the eval bar's own
   * source while `mode === 'assist'` (`stage/EvalBar/model.ts`), since
   * `lines` is cleared on every settle and would otherwise snap the bar to
   * 0 between searches. Same shape as one `evalsByPly` value, plus which
   * ply it belongs to.
   */
  assistSettled: {ply: number; score: number; isMate: boolean} | null;
  /** the manual toggle: on -> mode 'analysis', off -> mode 'off' */
  setOn: (on: boolean) => void;
  /** fix F-D / D3: `useAssistEffects` driving the mode directly ('assist') */
  setMode: (mode: AnalysisMode) => void;
  setPosition: (fen: string, ply: number) => void;
  /** one `info … pv` line, already converted to white-POV by `useEngine` */
  applyResult: (
    variation: number,
    line: EngineLine,
    searchedFen: string,
  ) => void;
  setBestMove: (bestMoveUci: string, searchedFen: string) => void;
  /**
   * fix L / L1: no-op outside assist mode; no-op if `fen` equals the tail's
   * (or the head's, while it is alone) — otherwise pushed, capped at
   * `ASSIST_QUEUE_MAX` by dropping the oldest NON-head item.
   */
  enqueueAssist: (fen: string, ply: number) => void;
  /**
   * fix L / L1: no-op unless `searchedFen` is the CURRENT head's fen; commits
   * the head's chip when settled (`bestmove`, or `watchdog` at depth >=
   * `ASSIST_MIN_SETTLED_DEPTH`) and shifts to the next position.
   */
  settleAssist: (searchedFen: string, reason: 'bestmove' | 'watchdog') => void;
  /** starts a branch at `basePly` (or restarts it, if browsing moved since) */
  pushBranchMove: (baseFen: string, basePly: number, move: BranchMove) => void;
  /** ± one branch move, clamped; cursor 0 does NOT drop the line */
  branchStep: (delta: -1 | 1) => void;
  /** the variation row's own click: jump the cursor to an exact branch move */
  setBranchCursor: (cursor: number) => void;
  clearBranch: () => void;
  reset: () => void;
};

const EMPTY: Pick<
  AnalysisStore,
  | 'fen'
  | 'ply'
  | 'lines'
  | 'bestMoveUci'
  | 'thinking'
  | 'terminal'
  | 'branch'
  | 'assistQueue'
  | 'assistSettled'
> = {
  fen: '',
  ply: 0,
  lines: [],
  bestMoveUci: null,
  thinking: false,
  terminal: null,
  branch: null,
  // fix L / L1: `setMode('off')` AND `reset()` clear the queue — but only
  // `reset()` also clears `evalsByPly` (below), so the chips an assisted
  // game already paid for survive its own end
  assistQueue: [],
  // fix Z1.2: cleared on the exact same two edges as `assistQueue` — the
  // bar has nothing to hold onto once assist mode itself is off
  assistSettled: null,
};

export const useAnalysis = create<AnalysisStore>((set, get) => ({
  mode: 'off',
  on: false,
  depth: ANALYSIS_DEPTH,
  evalsByPly: {},
  ...EMPTY,
  setOn: on => get().setMode(on ? 'analysis' : 'off'),
  setMode: mode =>
    set(s =>
      s.mode === mode
        ? s
        : // turning it OFF clears the readout AND the branch, but KEEPS
          // `evalsByPly`: the chips the player already paid for stay on the
          // notation
          {...s, mode, on: mode !== 'off', ...(mode === 'off' ? EMPTY : {})},
    ),
  setPosition: (fen, ply) =>
    set(s => {
      if (s.fen === fen && s.ply === ply) {
        return s;
      }
      // a new position invalidates the lines AND the arrow; anything still
      // in flight for the old one is dropped by the `searchedFen` guard.
      // fix U: a TERMINAL position never gets a search — `thinking` has
      // nothing to wait for, and `useEngine`'s own `terminalVerdict(fen)`
      // (same helper, same FEN) is what keeps `StockfishEngine` from ever
      // being asked in the first place.
      const terminal = terminalVerdict(fen);
      return {
        ...s,
        fen,
        ply,
        lines: [],
        bestMoveUci: null,
        terminal,
        thinking: !terminal,
      };
    }),
  applyResult: (variation, line, searchedFen) =>
    set(s => {
      // STALE GUARD: the engine answers for the position it searched, which in
      // the window between a browse and the restart is not the current one.
      if (!s.on || searchedFen !== s.fen) {
        return s;
      }
      const index = variation - 1;
      if (index < 0 || index >= ANALYSIS_LINES) {
        return s;
      }
      const lines = s.lines.slice();
      lines[index] = line;
      return {
        ...s,
        lines,
        thinking: false,
        // the BEST line's score is what the ply chip and the eval bar show —
        // only for a MAIN-LINE ply; a branch position is not one (F-C / C1).
        // fix L / L1: NEVER in assist mode — `settleAssist` alone commits
        // that chip, from a SETTLED search, never the deepening `info`
        // stream (two writers for one ply would race on every reply).
        evalsByPly:
          s.mode !== 'assist' && index === 0 && s.branch === null
            ? {
                ...s.evalsByPly,
                [s.ply]: {score: line.score, isMate: line.isMate},
              }
            : s.evalsByPly,
      };
    }),
  setBestMove: (bestMoveUci, searchedFen) =>
    set(s => (!s.on || searchedFen !== s.fen ? s : {...s, bestMoveUci})),
  enqueueAssist: (fen, ply) =>
    set(s => {
      if (s.mode !== 'assist') {
        return s;
      }
      const queue = s.assistQueue;
      const tail = queue.length > 0 ? queue[queue.length - 1] : null;
      // no-op if `fen` equals the LAST queued item's (or the head's, while
      // it is alone — which IS the tail when `queue.length === 1`)
      if (tail && tail.fen === fen) {
        return s;
      }
      if (queue.length === 0) {
        return {
          ...s,
          assistQueue: [{fen, ply, budgetMs: ASSIST_MOVETIME_MS}],
        };
      }
      // the pushed item's own budget is decided later, AT SHIFT TIME
      // (`settleAssist`) — it is never read while it sits behind the head
      let assistQueue = [...queue, {fen, ply, budgetMs: 0}];
      // a reconnect's multi-move jump must not queue 20 x 1s of catch-up:
      // drop the OLDEST non-head item (index 1) until it fits
      while (assistQueue.length > ASSIST_QUEUE_MAX) {
        assistQueue = [assistQueue[0], ...assistQueue.slice(2)];
      }
      return {...s, assistQueue};
    }),
  settleAssist: (searchedFen, reason) =>
    set(s => {
      const head = s.assistQueue[0];
      if (s.mode !== 'assist' || !head || searchedFen !== head.fen) {
        return s;
      }
      const line = s.lines[0];
      const settled =
        !!line &&
        (reason === 'bestmove' || line.depth >= ASSIST_MIN_SETTLED_DEPTH);
      const rest = s.assistQueue.slice(1);
      const nextHead = rest[0];
      const assistQueue = nextHead
        ? [
            {
              ...nextHead,
              // the backlog budget only while something STILL waits behind
              // the NEW head — decided once, here, never re-decided while it
              // stays head (a `movetime` prop edit restarts the search)
              budgetMs:
                rest.length > 1
                  ? ASSIST_BACKLOG_MOVETIME_MS
                  : ASSIST_MOVETIME_MS,
            },
            ...rest.slice(1),
          ]
        : [];
      return {
        ...s,
        assistQueue,
        lines: [],
        bestMoveUci: null,
        evalsByPly: settled
          ? {
              ...s.evalsByPly,
              [head.ply]: {score: line!.score, isMate: line!.isMate},
            }
          : s.evalsByPly,
        // fix Z1.2: the SAME values as the `evalsByPly` write above, so the
        // eval bar and the move list's chip can never disagree — unchanged
        // while not settled, so the PREVIOUS settled ply keeps the bar
        // steady for as long as the new head is still searching
        assistSettled: settled
          ? {ply: head.ply, score: line!.score, isMate: line!.isMate}
          : s.assistSettled,
      };
    }),
  pushBranchMove: (baseFen, basePly, move) =>
    set(s => {
      // a fresh branch, or the browsed ply moved since the last one — ONE
      // variation at a time, like chess.com's quick analysis (F-C intro)
      const branch =
        s.branch && s.branch.basePly === basePly
          ? s.branch
          : {basePly, baseFen, moves: [], cursor: 0};
      // a move played from an earlier cursor truncates whatever was ahead of
      // it, exactly like typing over the end of a line
      const moves = branch.moves.slice(0, branch.cursor);
      moves.push(move);
      return {...s, branch: {...branch, moves, cursor: moves.length}};
    }),
  branchStep: delta =>
    set(s => {
      if (!s.branch) {
        return s;
      }
      const cursor = Math.max(
        0,
        Math.min(s.branch.moves.length, s.branch.cursor + delta),
      );
      return cursor === s.branch.cursor
        ? s
        : {...s, branch: {...s.branch, cursor}};
    }),
  setBranchCursor: cursor =>
    set(s => {
      if (!s.branch) {
        return s;
      }
      const clamped = Math.max(0, Math.min(s.branch.moves.length, cursor));
      return clamped === s.branch.cursor
        ? s
        : {...s, branch: {...s.branch, cursor: clamped}};
    }),
  clearBranch: () => set(s => (s.branch === null ? s : {...s, branch: null})),
  reset: () =>
    set(s => ({...s, mode: 'off', on: false, evalsByPly: {}, ...EMPTY})),
}));

/**
 * Stop the engine and forget everything, from outside React — the Lobby and
 * New game actions, and the "a new game spawned" guard.
 */
export const resetAnalysis = (): void => {
  useAnalysis.getState().reset();
};

/** the eval of ONE ply, for the notation's per-ply chip (R4). */
export const selectPlyEval =
  (ply: number) =>
  (s: AnalysisStore): PlyEval | null =>
    s.on ? s.evalsByPly[ply] ?? null : null;

/**
 * The fen on the board (F-C / C4): the branch's tip, `baseFen` at cursor 0,
 * or null with no branch — `useEngine` and `BoardFrame` both fall back to the
 * browsed main-line position with `?? `.
 */
export const selectBranchTip = (s: AnalysisStore): string | null => {
  if (!s.branch) {
    return null;
  }
  return s.branch.cursor === 0
    ? s.branch.baseFen
    : s.branch.moves[s.branch.cursor - 1].fen;
};

/**
 * The branch's last move as squares, for a consumer that wants them
 * directly rather than diffing two FENs (`BoardFrame` diffs instead, since
 * that is the contract `Chessboard/index.web.tsx`'s `lastMoveFen` takes).
 */
export const selectBranchLastMove = (
  s: AnalysisStore,
): {from: string; to: string} | null => {
  if (!s.branch || s.branch.cursor === 0) {
    return null;
  }
  const {from, to} = s.branch.moves[s.branch.cursor - 1];
  return {from, to};
};

/** "+0.84" / "-1.20" / "M3" / "-M2" — the one place a score becomes copy. */
export const formatEval = (score: number, isMate: boolean): string => {
  if (isMate) {
    return `${score < 0 ? '-' : ''}M${Math.abs(score)}`;
  }
  const pawns = score / 100;
  return `${pawns > 0 ? '+' : pawns === 0 ? '' : '-'}${Math.abs(pawns).toFixed(
    2,
  )}`;
};

/**
 * fix U: the eval slot's copy for a terminal position — "Checkmate — White
 * wins" / "Stalemate — draw" / etc. The one place a `TerminalVerdict`
 * becomes copy, same spirit as `formatEval`.
 */
export const formatTerminalVerdict = (verdict: TerminalVerdict): string => {
  switch (verdict.kind) {
    case 'checkmate':
      return `Checkmate — ${verdict.winner === 'w' ? 'White' : 'Black'} wins`;
    case 'stalemate':
      return 'Stalemate — draw';
    case 'insufficient':
      return 'Draw — insufficient material';
    case 'repetition':
      return 'Draw — repetition';
    case 'fifty-move':
      return 'Draw — 50-move rule';
    default:
      return '';
  }
};

export default useAnalysis;
